<template>
    <DsModalDialog
        :cssClass="['ds-modal-dialog--video', canShowVideo ? 'ds-modal-dialog--gdpr-consented' : '']"
        :modelValue="isModalVisible"
        modalPosition="center"
        :animate="true"
        :theme="theme"
        @update:model-value="handleModalClosed"
        ref="dialogRef"
    >
        <template #content>
            <iframe
                v-if="canShowVideo"
                :src="embedVideoUrl"
                title="Video"
                class="ds-modal-dialog__iframe"
                allowfullscreen
                allow="autoplay; encrypted-media"
                loading="lazy"
            ></iframe>
            <DsModalVideoGdprOverlay v-else :videoUrl="embedVideoUrl" ref="overlayRef" />
        </template>
    </DsModalDialog>
</template>

<script setup lang="ts">
import { getCurrentInstance, nextTick, onMounted, onUnmounted, onUpdated, ref } from "vue";
import { useWecomaIntegration } from "@sazka/utils/gdpr/use-wecoma-integration";
import DsModalDialog from "../modal-dialog/ds-modal-dialog.vue";
import DsModalVideoGdprOverlay from "./ds-modal-video-gdpr-overlay.vue";
import { DsModalVideoProps } from "./ds-modal-video-props";
import { useDsModalVideoTools } from "./use-ds-modal-video-tools";
import { eventBus, GlobalEvents } from "@sazka/utils/global-events";
import { debounce } from "@sazka/utils/debounce";

const props = withDefaults(defineProps<DsModalVideoProps>(), {
    theme: "light",
    isInStorybook: false,
    cssClass: "",
});

const app = getCurrentInstance();
const overlayRef = ref<InstanceType<typeof DsModalVideoGdprOverlay> | null>(null);
const dialogRef = ref<InstanceType<typeof DsModalDialog> | null>(null);
const scrollHeight = ref(0);
const isModalVisible = ref(false);
const canShowVideo = ref(false);
const { embedVideoUrl } = useDsModalVideoTools(props.videoCodeOrUrl);
const { thirdPartyConsented } = useWecomaIntegration();

const checkForConsent = async () => {
    const consented = await thirdPartyConsented();
    canShowVideo.value = consented;
};

checkForConsent();

const handleModalClosed = () => {
    nextTick(() => {
        app.appContext.app.unmount();
    });
};

onMounted(() => {
    isModalVisible.value = true;
    eventBus.on(GlobalEvents.ConsentsChanged, checkForConsent);
});

function setMinHeight() {
    if (!dialogRef.value?.dialogBodyRef) return;

    dialogRef.value?.dialogBodyRef?.style.setProperty(
        "min-height",
        `calc(${scrollHeight.value}px + calc(var(--sp-dynamic-300, 24px) * 2))`
    );
}

function handleResize() {
    scrollHeight.value = overlayRef.value?.gdprOverlayContent.scrollHeight;
    setMinHeight();
}

const handleResizeDebounced = debounce(handleResize, 50);

onUpdated(() => {
    scrollHeight.value = overlayRef.value?.gdprOverlayContent.scrollHeight;
    setMinHeight();
});

window.addEventListener("resize", handleResizeDebounced);

onUnmounted(() => {
    eventBus.off(GlobalEvents.ConsentsChanged, checkForConsent);
    window.removeEventListener("resize", handleResizeDebounced);
});
</script>
